/**
 * React hook for managing the Patient Vault lifecycle.
 *
 * Provides a high-level API over PatientVault (encryption library) and
 * useBiometricAuth (WebAuthn), handling vault initialization, locking,
 * unlocking, record CRUD, and key backup/restore.
 *
 * The decrypted CryptoKey is held in React state (memory only) and is
 * discarded on lock or unmount -- it is never persisted in plaintext.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { PatientVault } from '@/lib/patient-vault';
import { useBiometricAuth } from './use-biometric-auth';

/** Metadata returned when listing vault records */
export interface VaultRecordMeta {
  id: string;
  storedAt: string;
  type: string;
}

/** Shape of a health record to be stored */
export interface HealthRecord {
  id: string;
  type: string;
  title: string;
  data: unknown;
  [key: string]: unknown;
}

interface PatientVaultHook {
  /** Whether the vault has been initialized (key exists in IndexedDB) */
  isInitialized: boolean;
  /** Whether the vault is currently unlocked (key in memory) */
  isUnlocked: boolean;
  /** Whether vault status is still loading */
  isLoading: boolean;
  /** Last error message */
  error: string | null;
  /** Clear the current error */
  clearError: () => void;

  /** Seed phrase shown once during initialization */
  seedPhrase: string | null;

  /** Initialize the vault: generate key, store it, return seed phrase */
  initializeVault: () => Promise<string>;
  /** Unlock the vault using biometric or password (seed phrase) */
  unlockVault: (
    method: 'biometric' | 'password',
    credential?: string,
  ) => Promise<boolean>;
  /** Lock the vault: clear key from memory */
  lockVault: () => void;

  /** Encrypt and store a health record */
  storeHealthRecord: (record: HealthRecord) => Promise<void>;
  /** Decrypt and return a health record by ID */
  getHealthRecord: (id: string) => Promise<unknown | null>;
  /** Delete a record */
  deleteHealthRecord: (id: string) => Promise<void>;
  /** List stored records (metadata only) */
  listRecords: () => Promise<VaultRecordMeta[]>;

  /** Export the vault key as a password-protected backup string */
  exportBackup: (password: string) => Promise<string>;
  /** Import a vault key from a backup string */
  importBackup: (encryptedKey: string, password: string) => Promise<void>;

  /** Biometric auth sub-state */
  biometric: {
    isSupported: boolean;
    isRegistered: boolean;
    register: () => Promise<boolean>;
    error: string | null;
  };
}

export function usePatientVault(): PatientVaultHook {
  const [isInitialized, setIsInitialized] = useState(false);
  const [isUnlocked, setIsUnlocked] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [seedPhrase, setSeedPhrase] = useState<string | null>(null);

  // Hold the decrypted key only in memory (ref to avoid unnecessary re-renders)
  const keyRef = useRef<CryptoKey | null>(null);

  const biometric = useBiometricAuth();

  // Check vault status on mount
  useEffect(() => {
    let cancelled = false;
    async function check() {
      try {
        const initialized = await PatientVault.isInitialized();
        if (!cancelled) {
          setIsInitialized(initialized);
        }
      } catch {
        // IndexedDB may be unavailable in some contexts
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    }
    check();
    return () => {
      cancelled = true;
    };
  }, []);

  // Clear key from memory on unmount
  useEffect(() => {
    return () => {
      keyRef.current = null;
    };
  }, []);

  const clearError = useCallback(() => setError(null), []);

  /**
   * Initialize a new vault: generate key, store it, produce seed phrase.
   */
  const initializeVault = useCallback(async (): Promise<string> => {
    setError(null);
    try {
      const key = await PatientVault.generateKey();
      await PatientVault.storeKey(key);

      // Store a verification token so seed phrase recovery can validate
      // the derived key before overwriting the master.
      await PatientVault.storeVerificationToken(key);

      keyRef.current = key;
      setIsUnlocked(true);
      setIsInitialized(true);

      const phrase = PatientVault.generateSeedPhrase();
      setSeedPhrase(phrase);
      return phrase;
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to initialize vault';
      setError(msg);
      throw err;
    }
  }, []);

  /**
   * Unlock the vault.
   * - 'biometric': triggers WebAuthn, then loads key from IndexedDB
   * - 'password': derives key from the provided seed phrase
   */
  const unlockVault = useCallback(
    async (method: 'biometric' | 'password', credential?: string): Promise<boolean> => {
      setError(null);
      try {
        if (method === 'biometric') {
          const ok = await biometric.authenticate();
          if (!ok) {
            setError(biometric.error || 'Biometric authentication failed');
            return false;
          }
          // Biometric succeeded -- load key from IndexedDB
          const storedKey = await PatientVault.getStoredKey();
          if (!storedKey) {
            setError('Vault key not found. The vault may need to be restored from backup.');
            return false;
          }
          keyRef.current = storedKey;
          setIsUnlocked(true);
          return true;
        }

        // Password / seed phrase flow
        if (!credential) {
          setError('Please provide your seed phrase or recovery password.');
          return false;
        }

        const derivedKey = await PatientVault.deriveKeyFromSeed(credential);

        // Verify the derived key can decrypt the verification token before
        // accepting it as the new master key. This prevents overwriting a
        // valid master key with a key derived from an incorrect seed phrase.
        const isValid = await PatientVault.verifyKey(derivedKey);
        if (!isValid) {
          setError('Invalid seed phrase. The derived key could not decrypt the vault.');
          return false;
        }

        await PatientVault.storeKey(derivedKey);
        keyRef.current = derivedKey;
        setIsUnlocked(true);
        setIsInitialized(true);
        return true;
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Failed to unlock vault';
        setError(msg);
        return false;
      }
    },
    [biometric],
  );

  /**
   * Lock the vault: clear the key from memory.
   */
  const lockVault = useCallback(() => {
    keyRef.current = null;
    setIsUnlocked(false);
    setSeedPhrase(null);
  }, []);

  /**
   * Encrypt and store a health record.
   */
  const storeHealthRecord = useCallback(async (record: HealthRecord) => {
    if (!keyRef.current) {
      throw new Error('Vault is locked');
    }
    const encrypted = await PatientVault.encrypt(keyRef.current, record);
    await PatientVault.storeRecord(record.id, encrypted, record.type);
  }, []);

  /**
   * Retrieve and decrypt a health record.
   */
  const getHealthRecord = useCallback(async (id: string): Promise<unknown | null> => {
    if (!keyRef.current) {
      throw new Error('Vault is locked');
    }
    return PatientVault.getRecord(keyRef.current, id);
  }, []);

  /**
   * Delete a health record from the vault.
   */
  const deleteHealthRecord = useCallback(async (id: string): Promise<void> => {
    await PatientVault.deleteRecord(id);
  }, []);

  /**
   * List all stored records (metadata only, no decryption).
   */
  const listRecords = useCallback(async (): Promise<VaultRecordMeta[]> => {
    return PatientVault.listRecords();
  }, []);

  /**
   * Export the vault key as a password-protected string.
   */
  const exportBackup = useCallback(async (password: string): Promise<string> => {
    if (!keyRef.current) {
      throw new Error('Vault is locked');
    }
    return PatientVault.exportKeyForBackup(keyRef.current, password);
  }, []);

  /**
   * Import a vault key from a password-protected backup.
   */
  const importBackup = useCallback(
    async (encryptedKey: string, password: string): Promise<void> => {
      setError(null);
      try {
        const key = await PatientVault.importKeyFromBackup(encryptedKey, password);
        await PatientVault.storeKey(key);
        keyRef.current = key;
        setIsUnlocked(true);
        setIsInitialized(true);
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Failed to import backup';
        setError(msg);
        throw err;
      }
    },
    [],
  );

  return {
    isInitialized,
    isUnlocked,
    isLoading,
    error,
    clearError,
    seedPhrase,
    initializeVault,
    unlockVault,
    lockVault,
    storeHealthRecord,
    getHealthRecord,
    deleteHealthRecord,
    listRecords,
    exportBackup,
    importBackup,
    biometric: {
      isSupported: biometric.isSupported,
      isRegistered: biometric.isRegistered,
      register: biometric.register,
      error: biometric.error,
    },
  };
}
